<template>
  <div
    v-if="shouldShowSnackbar && qualifyingAlert"
    class="alerts-snackbar"
    data-qa="alerts-snackbar"
    role="status"
    aria-live="polite"
  >
    <p class="alerts-snackbar__title" data-qa="alerts-snackbar-title">
      {{ qualifyingAlert.title }}
    </p>
    <MintButton
      class="alerts-snackbar__action"
      type="button"
      theme="text-link"
      data-qa="alerts-snackbar-view"
      @click.prevent="viewAlert"
    >
      View
    </MintButton>
  </div>
</template>

<script setup lang="ts">
  import MintButton from '@mony-group/mint-button';
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

  import { useAlertsStore } from '@/stores';
  import { AlertWrapper } from '@/types/responseTypes';

  interface Props {
    isBlocked?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    isBlocked: false,
  });

  const alertsStore = useAlertsStore();

  const isDesktopViewport = ref(false);
  const dismissedSnackbarAlertId = ref<number | null>(null);

  let dismissTimer: ReturnType<typeof setTimeout> | null = null;

  const qualifyingAlert = computed<AlertWrapper | null>(() => {
    if (isDesktopViewport.value) return null;

    return alertsStore.snackbarAlert || null;
  });

  const qualifyingAlertId = computed(() => qualifyingAlert.value?.alertId ?? null);
  
  const hasQualifyingAlert = computed(() => qualifyingAlert.value !== null);

  const isDismissedAlert = computed(() => qualifyingAlertId.value !== null && dismissedSnackbarAlertId.value === qualifyingAlertId.value );

  const isBlockedAlert = computed(() => props.isBlocked || alertsStore.selectedAlertId !== null );

  const shouldShowSnackbar = computed(() =>
    hasQualifyingAlert.value &&
    alertsStore.hasLoaded &&
    !alertsStore.isError &&
    !isBlockedAlert.value &&
    !isDismissedAlert.value,
  );

  const stopAutoDismiss = () => {
    if (dismissTimer) {
      clearTimeout(dismissTimer);
      dismissTimer = null;
    }
  };

  const dismissCurrentAlert = () => {
    if (qualifyingAlertId.value === null) return;

    dismissedSnackbarAlertId.value = qualifyingAlertId.value;
    stopAutoDismiss();
  };

  const scheduleAutoDismiss = () => {
    stopAutoDismiss();

    dismissTimer = setTimeout(() => {
      dismissCurrentAlert();
    }, 10000);
  };

  const viewAlert = () => {
    const alert = qualifyingAlert.value;

    if (!alert) return;

    dismissCurrentAlert();
    alertsStore.openAlertDetail(alert.alertId);

    if (!alert.viewed) {
      void alertsStore.markAlertAsViewed(alert.alertId);
    }
  };

  onMounted(() => {
    isDesktopViewport.value = window.matchMedia('(min-width: 760px)').matches;
  });

  watch(
    () => [shouldShowSnackbar.value, qualifyingAlertId.value] as const,
    ([isVisible, alertId]) => {
      if (!isVisible || alertId === null) {
        stopAutoDismiss();
        return;
      }

      scheduleAutoDismiss();
    },
    { immediate: true },
  );

  onBeforeUnmount(() => {
    stopAutoDismiss();
  });
</script>

<style scoped lang="scss">
  .alerts-snackbar {
    position: fixed;
    top: 110px;
    left: 16px;
    right: 16px;
    z-index: 12;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 16px;
    background: $brand-primary-100-white;
    border-radius: 8px;
    box-shadow:
      0 1px 3px rgb(0 0 0 / 50%),
      0 3px 8px rgb(0 0 0 / 35%),
      inset 0 0 0.5px rgb(255 255 255 / 22%),
      inset 0 0.5px 0 rgb(255 255 255 / 5%);

    @include new-md {
      display: none;
    }

    &__title {
      flex: 1;
      margin: 0;
      color: $brand-primary-100-navy;
      font-family: $helvetica;
      font-size: remc(14px);
      font-weight: 500;
      line-height: remc(20px);
    }

    &__action {
      flex-shrink: 0;
      min-height: auto;
      padding: 0;

      :deep(.mint-button__text) {
        font-size: remc(14px);
        font-weight: 500;
        line-height: remc(20px);
        text-decoration: underline;
      }
    }
  }
</style>
